<script setup lang="ts">
/**
 * CampaignDonorsBanner — bandeau "Déjà N participants, rejoignez-les !" avec
 * les avatars empilés des 3 premiers donateurs.
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps malgré leurs thèmes différents.
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignDonorsBanner from "@donoor/ui/campaign/CampaignDonorsBanner.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
import type { CampaignDonor } from "./types";

// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`,
// présentes dans donoor.org ET dashboard.donoor.org (useI18n auto-importé).
// `locale` sert aussi à formater `donorsTotal` (séparateur de milliers) :
// avant ce lot, `"fr-FR"` était codé en dur, quelle que soit la langue.
const { t, locale } = useI18n();

defineProps<{
  donors: CampaignDonor[];
  donorsTotal: number;
}>();
</script>

<template>
  <div
    class="bg-linear-to-r from-[#0F3D8C] to-[#0F3D8C]/80 dark:from-[#1f9afe]/20 dark:to-[#1f9afe]/10 rounded-full px-4 py-3 border border-[#1f9afe]/20"
  >
    <div class="flex items-center gap-4 min-w-0">
      <div class="flex -space-x-3 shrink-0 pe-2">
        <div
          class="w-8 h-8 rounded-full bg-[#1f9afe] flex items-center justify-center text-white text-xs font-bold ring-2 ring-white dark:ring-gray-800"
        >
          {{ donors[0]?.name?.charAt(0)?.toUpperCase() || "?" }}
        </div>
        <div
          v-if="donors[1]"
          class="w-8 h-8 rounded-full bg-[#1f9afe] flex items-center justify-center text-white text-xs font-bold ring-2 ring-white dark:ring-gray-800"
        >
          {{ donors[1]?.name?.charAt(0)?.toUpperCase() || "?" }}
        </div>
        <!-- 3e avatar masqué sous 480px pour laisser de la place au texte -->
        <div
          v-if="donors[2]"
          class="w-8 h-8 rounded-full bg-[#1976D2] hidden xs:flex items-center justify-center text-white text-xs font-bold ring-2 ring-white dark:ring-gray-800"
        >
          {{ donors[2]?.name?.charAt(0)?.toUpperCase() || "?" }}
        </div>
      </div>
      <p class="text-white dark:text-gray-200 font-medium text-sm md:text-base min-w-0">
        <!-- « Déjà » masqué sous 480px (gain de place, texte tient sur 1 ligne) -->
        <span class="hidden xs:inline">{{ t("ui.donorsBanner.deja") }}</span>
        <span class="font-bold text-[#D7EBFF]">{{ donorsTotal.toLocaleString(locale) }}</span>
        {{ t("ui.donorsBanner.rejoignezNous", donorsTotal) }}
      </p>
    </div>
  </div>
</template>
